import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { RouterProvider } from 'react-router-dom';
import { router } from './routes';
import { FeatureFlagsProvider } from './FeatureFlagsProvider';
import { ProgressProvider } from './contexts/ProgressProvider';
import { getInitialParam } from './utils/initialParams';
import { prefetchPlayerCredentials } from './lib/playerPrefetch';

type SentryConfig = {
  dsn: string;
  environment: string;
};

// Fetch Sentry configuration from server
async function fetchSentryConfig(): Promise<SentryConfig | null> {
  try {
    const response = await fetch('/internal/sentry/config');
    if (!response.ok) {
      console.warn('Failed to fetch Sentry config from server');
      return null;
    }
    return await response.json();
  } catch (error) {
    console.warn('Error fetching Sentry config:', error);
    return null;
  }
}

// Initialize Sentry with config from server
async function initializeSentry() {
  const sentryConfig = await fetchSentryConfig();

  // Initialize Sentry with either server config or fallback to env vars
  Sentry.init({
    dsn: sentryConfig?.dsn || import.meta.env.VITE_SENTRY_DSN,
    environment: sentryConfig?.environment || import.meta.env.MODE,
    release: import.meta.env.SENTRY_RELEASE,
    enableLogs: true,
    integrations: [
      Sentry.consoleLoggingIntegration(),
      Sentry.replayIntegration(),
      Sentry.browserTracingIntegration(),
    ],
    debug: import.meta.env.MODE !== 'production',
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      if (import.meta.env.MODE !== 'production') {
        if (
          event.exception?.values?.[0]?.value?.includes(
            'Failed to retrieve data'
          )
        ) {
          console.log('Filtering out development API error');
          return null;
        }
      }

      if (
        typeof hint.originalException === 'object' &&
        hint.originalException != null
      ) {
        event.extra = {
          ...event.extra,
          ...(hint.originalException as Record<string, unknown>),
        };
      }

      return event;
    },
  });
}

// App component with loading state
export function App() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    (async () => {
      await initializeSentry();
      const player = getInitialParam('player');
      if (player) await prefetchPlayerCredentials(player);
      setIsInitialized(true);
    })();
  }, []);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-ink-muted">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <FeatureFlagsProvider>
      <ProgressProvider>
        <Sentry.ErrorBoundary
          // oxlint-disable-next-line no-unused-vars
          fallback={({ error }) => (
            <div className="min-h-screen flex items-center justify-center bg-paper">
              <div className="text-center">
                <h1 className="font-display text-2xl font-bold text-ink mb-2">
                  Something went wrong
                </h1>
                <p className="text-ink-muted mb-4">
                  We've been notified about this error.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-primary hover:bg-primary-deep text-white rounded-xl font-semibold transition-colors"
                >
                  Reload page
                </button>
              </div>
            </div>
          )}
        >
          <RouterProvider router={router} />
        </Sentry.ErrorBoundary>
      </ProgressProvider>
    </FeatureFlagsProvider>
  );
}
